import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import {
  Button,
  FormConditionRadioGroup,
  FormTextField,
  SelectOption,
} from "@covertree/ui";
import FormPhoneField from "@covertree/ui/forms/FormPhoneField";
import {
  AddressAutocompletePrecisely,
  PreciselyAddressWithUnit,
} from "@covertree/features";
import { yesNoToBoolean } from "@covertree/utils";

const newInjuredPerson = {
  firstName: "",
  lastName: "",
  address: {
    value: "",
    label: "",
  },
  unit: "",
  phoneNumber: "",
};

interface InjuredPersonsSectionProps {
  label?: string;
}

export const InjuredPersonsSection = ({
  label = "Was anyone injured?",
}: InjuredPersonsSectionProps) => {
  const { control, watch, setValue } = useFormContext();
  const { fields, remove, append } = useFieldArray({
    control,
    name: "injuredPeople",
  });
  const wasAnyoneInjured = yesNoToBoolean(watch("wasAnyoneInjured"));

  const handleConfirmClick = useCallback(() => {
    if (fields.length === 0) {
      append(newInjuredPerson);
    }
  }, [fields, newInjuredPerson, append]);

  const handleDeclineClick = useCallback(() => remove(), [remove]);

  return (
    <Box>
      <Box pt={3}>
        <FormConditionRadioGroup
          id="was-anyone-injured"
          name="wasAnyoneInjured"
          label={label}
          control={control}
          onConfirmClick={handleConfirmClick}
          onDeclineClick={handleDeclineClick}
        />
      </Box>

      {fields.map((field, index) => (
        <Box
          mt={3}
          key={field.id}
          border={(theme) => `1px solid ${theme.colors.grey3}`}
          borderRadius={2}
          p={3}>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Typography>Person {index + 1}</Typography>
            </Grid>
            {fields.length > 1 && (
              <Grid item>
                <IconButton
                  sx={(theme) => ({
                    padding: theme.spacing(1),
                    border: `1px solid ${theme.colors.grey3}`,
                  })}
                  data-testid={`remove-person-btn-${index}`}
                  onClick={() => remove(index)}
                  size="large">
                  <CloseIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormTextField
                id="injured-person-first-name"
                dataTestId="injured-person-first-name"
                name={`injuredPeople.${index}.firstName`}
                label="First name"
                control={control}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                id="injured-person-last-name"
                dataTestId="injured-person-last-name"
                name={`injuredPeople.${index}.lastName`}
                label="Last name"
                control={control}
              />
            </Grid>
            <Grid item md={8} xs={12}>
              <AddressAutocompletePrecisely
                id="injured-person-address"
                dataTestId="injured-person-address"
                name={`injuredPeople.${index}.address`}
                label="Address"
                control={control}
                onAutocompleteChange={(
                  _e,
                  option: SelectOption<PreciselyAddressWithUnit>
                ) => {
                  setValue(
                    `injuredPeople.${index}.lotUnit`,
                    option.value.lotUnit ?? ""
                  );
                }}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormTextField
                id="injured-person-unit"
                dataTestId="injured-person-unit"
                name={`injuredPeople.${index}.lotUnit`}
                label="Unit / Lot#"
                control={control}
                optional
              />
            </Grid>
            <Grid item xs={12}>
              <FormPhoneField
                id="phone-number"
                dataTestId="phone-number"
                name={`injuredPeople.${index}.phoneNumber`}
                label="Phone number"
                control={control}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
      {wasAnyoneInjured && (
        <Box pt={3}>
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={() => append(newInjuredPerson)}>
            Add injured
          </Button>
        </Box>
      )}
    </Box>
  );
};
